import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { ROUTES } from '#/config/routes';
import { approveStockIssue } from '#/api/imsMgmt/receipts/stockIssue/approve';
import { cancelStockIssue } from '#/api/imsMgmt/receipts/stockIssue/cancel';
import { rejectStockIssue } from '#/api/imsMgmt/receipts/stockIssue/reject';
import { submitStockIssue } from '#/api/imsMgmt/receipts/stockIssue/submit';
import {
  approveWorkOrderStockIssue,
  cancelWorkOrderStockIssue,
  rejectWorkOrderStockIssue,
  submitWorkOrderStockIssue,
} from '#/api/momMgmt/workOrder/outbound';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  getStockIssueApiErrorMessage,
  isStockIssueVersionConflict,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/apiError';
import {
  canApproveStockIssue,
  canCancelStockIssue,
  canEditStockIssueDraft,
  canEditStockIssueRemark,
  canRejectStockIssue,
  canSubmitStockIssue,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import { toast } from '#/utils/toast';
import {
  stockIssueDetailQueryKey,
  stockIssueListQueryKeyPrefix,
  workOrderDetailQueryKey,
} from '#/utils/queryKeys';

function resolveWorkOrderId(detail) {
  if (detail?.sourceType === 'WORK_ORDER' && detail?.sourceId) {
    return detail.sourceId;
  }
  return null;
}

const StockIssueCommandBar = ({ detail, onRefetch }) => {
  const canCreate = useCan(PERM.STOCK_ISSUE_CREATE);
  const canUpdate = useCan(PERM.STOCK_ISSUE_UPDATE);
  const canReview = useCan(PERM.STOCK_ISSUE_REVIEW);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;
  const workOrderId = resolveWorkOrderId(detail);
  const useNestedWorkflow = Boolean(workOrderId);

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: stockIssueListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: stockIssueDetailQueryKey(id) });
    }
    if (workOrderId) {
      await queryClient.invalidateQueries({
        queryKey: workOrderDetailQueryKey(workOrderId),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (useNestedWorkflow) {
        if (action === 'submit') {
          return submitWorkOrderStockIssue(workOrderId, id, payload);
        }
        if (action === 'approve') {
          return approveWorkOrderStockIssue(workOrderId, id, payload);
        }
        if (action === 'reject') {
          return rejectWorkOrderStockIssue(workOrderId, id, payload);
        }
        if (action === 'cancel') {
          return cancelWorkOrderStockIssue(workOrderId, id);
        }
      } else {
        if (action === 'submit') {
          return submitStockIssue(id, payload);
        }
        if (action === 'approve') {
          return approveStockIssue(id, payload);
        }
        if (action === 'reject') {
          return rejectStockIssue(id, payload);
        }
        if (action === 'cancel') {
          return cancelStockIssue(id);
        }
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'cancel') {
        await queryClient.invalidateQueries({ queryKey: stockIssueListQueryKeyPrefix() });
        if (workOrderId) {
          await queryClient.invalidateQueries({
            queryKey: workOrderDetailQueryKey(workOrderId),
          });
        }
        navigate(ROUTES.stockIssue);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getStockIssueApiErrorMessage(error));
      if (isStockIssueVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交出庫單',
      message: '提交後將鎖定來源庫存並進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准出庫單',
      message: '核准後將扣減庫存，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel' });
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回出庫單"
        description="退回後將恢復為草稿並釋放鎖定，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      {canEditStockIssueDraft(status) && canUpdate ? (
        <Button asChild variant="default" className={cn('document-detail-edit-action')}>
          <Link to={ROUTES.stockIssueEdit(id)} aria-label="編輯">
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canEditStockIssueRemark(status) && canUpdate ? (
        <Button asChild variant="default" className={cn('document-detail-edit-action')}>
          <Link to={ROUTES.stockIssueRemark(id)} aria-label="編輯備註">
            <Pencil className="size-3.5" aria-hidden />
            編輯備註
          </Link>
        </Button>
      ) : null}
      {canSubmitStockIssue(status) && canCreate ? (
        <Button disabled={isPending} onClick={handleSubmit}>
          提交
        </Button>
      ) : null}
      {canApproveStockIssue(status) && canReview ? (
        <Button disabled={isPending} onClick={handleApprove}>
          核准
        </Button>
      ) : null}
      {canRejectStockIssue(status) && canReview ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRejectDialogOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canCancelStockIssue(status) && canCreate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleCancel}
        >
          刪除草稿
        </Button>
      ) : null}
    </>
  );
};

StockIssueCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    remark: PropTypes.string,
    updatedAt: PropTypes.string,
    sourceType: PropTypes.string,
    sourceId: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default StockIssueCommandBar;
